import { Fragment, useCallback, useMemo, useState } from 'react';
import { JourneyOperationDialog } from './JourneyOperationDialog';
import { JourneyPendingActions } from './JourneyPendingActions';
import type { JourneyIntent } from './journeyOperations';
import { useJourneyAdmin } from './useJourneyAdmin';
import { useProspectingAvailability } from './useProspectingAvailability';
import { useMergeLeads } from './hooks';
import { Navigate, useSearchParams } from 'react-router';
import { Badge, Button, EmptyState, InlineError, Panel, Skeleton } from '../../components/ui';
import { PageHeader } from '../../layouts/Shells';
import { useTapPeople } from '../projects/hooks';
import { useAuth } from '../auth/AuthProvider';
import { CompaniesView, CompanyPanel } from './CompaniesView';
import { CopyLibraryView } from './CopyLibraryView';
import { DashboardView } from './DashboardView';
import { FollowUpPrompt } from './FollowUpPrompt';
import { ImportDrawer, NewLeadDrawer } from './LeadDrawers';
import { useAllActivity, useAllAdminActivity, useAllProspectActivity } from './activityStats';
import { duplicateLeadGroups } from './leadDuplicates';
import { PipelineView } from './PipelineView';
import { pipelineSliceLabel } from './leadPipeline';
import { StartRoundsForSelected } from './StartRoundsForSelected';
import { AddSelectedToList } from './AddSelectedToList';
import { RoundBar } from './RoundBar';
import { RowOriginCell } from './RowOriginCell';
import { SortHeader, ariaSortFor } from './SortHeader';
import { BatchUndoStrip } from './BatchUndoStrip';
import { LeadFilterBar } from './LeadFilterBar';
import { LeadPanel } from './LeadPanel';
import { ProspectingView } from './ProspectingView';
import { RoundsView } from './RoundsView';
import { OutreachView } from './OutreachView';
import { ReportsView } from './ReportsView';
import { dealOutcomeLabel, dealOutcomeTone, useLeadDealOutcomes } from './dealOutcomes';
import { positionIsDerived, positionLabel, positionOf, positionTone } from './leadPosition';
import {
  useAllMemberships,
  useLeadLists,
  useCompanies,
  useLeadStages,
  useLeads,
  useSalesAccess,
  useUpdateLead,
} from './hooks';
import { activityErrorMessage, lastTouchLabel, useLogLeadActivity } from './leadActivity';
import {
  categoriesOf,
  leadFieldErrorMessage,
  nameOf,
  useCategoryLinks,
  useClosureReasons,
  useLeadCategories,
  useLeadSources,
  useMarkContacted,
} from './leadFields';
import { originGroupById, originGroups } from './leadOriginFilter';
import { sortLeads } from './leadSort';
import type { LeadSort } from './leadSort';
import {
  EMPTY_FILTER,
  EMPTY_ROUNDS,
  activeLeadTab,
  filterLeads,
  leadTabCounts,
} from './leadFilters';
import { evidenceByLead, evidenceGroups } from './leadEvidence';
import {
  batchErrorMessage,
  useBulkFillLeadSource,
  useRecentLeadSourceBatches,
  useRevertLeadSourceBatch,
} from './leadSourceBatches';
import type { LeadFilter, RoundContext } from './leadFilters';
import { dueLabel, isOverdue, nextActionByLead, useLeadFollowups } from './leadFollowups';
import { useRoundSweep, useRunningRounds } from './leadRounds';
import {
  collapsedRowCount,
  groupLeadsByCompany,
  groupSummary,
  looksMislinked,
  readGroupByCompany,
  writeGroupByCompany,
} from './leadGrouping';
import {
  groupLeadsByRound,
  readRoundsView,
  roundGroupSummary,
  writeRoundsView,
} from './leadRoundView';
import {
  isRunningOut,
  isDueToday,
  loopCellLabel,
  loopStateOf,
  progressOf,
  useLeadLoops,
} from './leadLoop';
import { leadStageKey, newLeadStage, stageByKey } from './stageKeys';
import { shortCategory, todayIso } from './trackerFormat';
import { TAB_LABELS } from './trackerTabs';
import { DrillBanner } from './DrillBanner';
import type { Drill } from './drill';
import type { TrackerTab } from './trackerTabs';
import { SOURCE_LABELS, SOURCE_TONES, leadName, salesErrorMessage } from './types';
import type { Lead, Membership } from './types';
import './leadsTable.css';

/**
 * The Sales Tracker page: access check, tab state, and the Leads list.
 *
 * This file was 5,689 lines holding nine tabs, every drawer and every panel in
 * the module. Each tab is now its own file beside this one; what stays here is
 * the shell — who may see the page, which tab is showing, the state the Leads
 * list and the lead panel share, and the Leads list itself.
 *
 * The split moved code and changed nothing else. No component gained or lost a
 * prop, no hook moved, no order of calls changed. That is what makes it
 * checkable: if anything at all behaves differently, the move is wrong.
 */

export function TrackerPage() {
  // Deals moved to its own page. The sidebar pointed here for months, so
  // anybody who bookmarked the tab keeps working instead of landing on a
  // Dashboard they did not ask for.
  const [redirectParams] = useSearchParams();
  if (redirectParams.get('tab') === 'deals') {
    return <Navigate to="/deals" replace />;
  }
  // Same move for Tasks, 14 September. The tab is gone and its work lives at
  // /sales-tasks, which holds strictly more than the tab did: deal and
  // prospect tasks as well as the tracker's own. Without this a bookmark
  // lands on the Dashboard and looks like the tasks were deleted.
  if (redirectParams.get('tab') === 'tasks') {
    return <Navigate to="/sales-tasks" replace />;
  }

  return <TrackerPageInner />;
}

/**
 * How many columns the leads table has, for the company group row's colSpan.
 *
 * A number that silently goes wrong: add a column, forget this, and the group
 * header stops spanning the table with no error anywhere. A test counts the
 * `<th>` elements in the header against it.
 */
const LEAD_TABLE_COLUMNS = 13;

function TrackerPageInner() {
  const accessQuery = useSalesAccess();
  const stagesQuery = useLeadStages();
  const leadsQuery = useLeads();
  const allMembershipsQuery = useAllMemberships();
  // The Pipeline reads the timeline for replies and last-touch dates. Same
  // query the Dashboard uses, so it is a cache hit rather than a second fetch.
  const allActivityQuery = useAllActivity();
  const prospectActivityQuery = useAllProspectActivity();
  const adminActivityQuery = useAllAdminActivity();
  const [mergeIntents, setMergeIntents] = useState<JourneyIntent[] | null>(null);
  const merge = useMergeLeads();
  const { operations_enabled } = useProspectingAvailability();
  const listsQuery = useLeadLists();
  // Only so a company group can be headed by the RECORD's name rather than by
  // whichever lead sorted first. One record holds 19 leads carrying 18
  // different company names, and the first-lead heading named none of them.
  const companiesQuery = useCompanies();
  // The sidebar links straight to a tab, so the URL decides the initial one.
  // Read once rather than kept in sync: the tabs are in-page navigation, and
  // rewriting the URL on every click would fill the back button with them.
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const [tab, setTab] = useState<TrackerTab>(
    requestedTab !== null && Object.hasOwn(TAB_LABELS, requestedTab)
      ? (requestedTab as TrackerTab)
      : 'dashboard',
  );
  // The Leads list's own filter (batch 1b). Held here rather than in the bar
  // so a saved view survives opening a lead and coming back.
  const [leadFilter, setLeadFilter] = useState<LeadFilter>(EMPTY_FILTER);
  const leadCategories = useLeadCategories();
  const leadOrigins = useLeadSources();
  // Two jobs, one query: the "Closed because" chips, and the guard that keeps
  // a Do-not-contact lead out of a campaign list. Both must read the same
  // vocabulary — a second subscription is how the filter and the guard end up
  // disagreeing about which reason means stop.
  const closureReasons = useClosureReasons();
  const categoryLinks = useCategoryLinks();
  // For the leads Nez emailed from Gmail with nothing recorded. The Hub holds
  // no evidence of those, so a person says so once rather than a migration
  // inventing it.
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(new Set());
  /**
   * Gather leads under the company they share — OFF unless somebody turns it
   * on. Measured before building: 160 of 244 companies have exactly one lead,
   * and grouping compresses only the untouched backlog. So it is a view for a
   * job, not the shape of the table. See leadGrouping.ts.
   */
  const [groupByCompany, setGroupByCompany] = useState<boolean>(() => readGroupByCompany());
  // The Rounds work queue. ONE GROUPING AT A TIME: turning either view on
  // turns the other off, because two groupings at once has no sensible
  // drawing — a lead cannot sit under a company header and a round header in
  // the same table.
  const [roundsView, setRoundsView] = useState<boolean>(() => readRoundsView());
  /**
   * Which column the list is ordered by, if any.
   *
   * NOT persisted, unlike Group by company and the Rounds view. Those two
   * change what the list IS; a sort only changes what order you read it in,
   * and a remembered order is a thing that surprises you tomorrow when you
   * cannot remember asking for it.
   */
  const [sort, setSort] = useState<LeadSort | null>(null);
  /**
   * Sorting turns the two grouped views off.
   *
   * Kyu approved this rather than it being discovered. Both views impose an
   * order of their own — a company group sits at the position of its first
   * lead, and the Rounds view is deliberately worst-off first — so a column
   * sort applied underneath them would either be ignored or would silently
   * break the thing that makes them worth having. Off is honest; fighting is
   * not.
   */
  const applySort = useCallback((next: LeadSort | null) => {
    setSort(next);
    if (next === null) {
      return;
    }
    setGroupByCompany(false);
    writeGroupByCompany(false);
    setExpandedCompanies(new Set());
    setRoundsView(false);
    writeRoundsView(false);
  }, []);
  // Which company groups are open. Page state, not remembered: expanding is a
  // momentary act, unlike the decision to group at all.
  const [expandedCompanies, setExpandedCompanies] = useState<Set<string>>(new Set());
  const dealOutcomes = useLeadDealOutcomes();
  const markContacted = useMarkContacted();
  // Logging from the row, not only from the drawer.
  //
  // Alex, looking at leads he knew were being worked: "is something not
  // tracking right, because this is last touch and these all say never." The
  // number was honest — nothing had ever been recorded. The drawer already had
  // a one-click button; what cost too much was finding the lead, opening the
  // drawer, and then clicking. Kyu: the team sends from Gmail and returns to
  // the Hub within seconds, so the fix is reach, not a different design.
  //
  // Held per lead rather than as one boolean: a shared flag would put every
  // row in a 780-row table into "Logging…" while one of them saved.
  const logFromRow = useLogLeadActivity();
  const [loggingLeadId, setLoggingLeadId] = useState('');
  // Offered above the table rather than inside the row: the prompt is four
  // buttons wide and a row is 40px tall. Named, so it is never ambiguous
  // which lead is about to get the follow-up.
  const [followUpFor, setFollowUpFor] = useState<{ id: string; name: string } | null>(null);
  const followupsQuery = useLeadFollowups();
  // Nothing in a database notices that a day has gone by, so a round whose
  // window ran out on Friday is still open on Monday until somebody asks.
  // Asked once here, when the Tracker loads and once access is confirmed —
  // and once only, because the query key carries today's date.
  useRoundSweep(accessQuery.data === true);
  // The two halves of the Rounds column and the loop filters. Both are whole
  // small tables rather than a query per row: one is bounded by the leads
  // being worked right now, the other by the leads ever worked, and neither
  // grows with the 691 that exist.
  const runningRounds = useRunningRounds();
  const leadLoops = useLeadLoops();
  // Only to put a name on an import batch — "added by Karl" beats a uuid.
  // Enabled on the Leads tab alone; the other seven never ask.
  // Fetched on the Pipeline tab as well as Leads: the actions funnel puts a
  // name beside each person's row, and without this it would draw ids.
  const people = useTapPeople(tab === 'leads' || tab === 'pipeline');
  const { access } = useAuth();
  const journeyAdmin = useJourneyAdmin();
  const bulkFill = useBulkFillLeadSource();
  const revertBatch = useRevertLeadSourceBatch();
  const recentBatches = useRecentLeadSourceBatches(tab === 'leads');
  // Reverting is admin-only at the database. Hiding the control for everybody
  // else beats offering a button that always errors.
  const isAdmin =
    accessQuery.data === true &&
    access.status === 'approved' &&
    access.profile.internal_role === 'tap_admin';
  /**
   * The two lookups the column sort reads, memoised.
   *
   * Built here rather than inside the render body because the comparator
   * touches them once per COMPARISON — roughly n log n times on 693 rows —
   * and rebuilding a Map inside that is how a sort starts feeling slow on the
   * machine it is meant to help.
   */
  const companyNamesById = useMemo(
    () => new Map((companiesQuery.data ?? []).map((company) => [company.id, company.name])),
    [companiesQuery.data],
  );
  const nextActions = useMemo(
    () => nextActionByLead(followupsQuery.data ?? []),
    [followupsQuery.data],
  );
  const nextActionDueByLead = useMemo(
    () => new Map([...nextActions].map(([leadId, action]) => [leadId, action.due_date])),
    [nextActions],
  );
  // One map for both filterLeads and leadTabCounts, so the tab numbers and the
  // list can never disagree about which leads are social.
  const originGrouping = useMemo(
    () => originGroupById(originGroups(leadOrigins.data ?? [])),
    [leadOrigins.data],
  );

  const roundContext = useMemo<RoundContext>(
    () => ({
      running: runningRounds.data ?? EMPTY_ROUNDS.running,
      loops: leadLoops.data ?? EMPTY_ROUNDS.loops,
    }),
    [runningRounds.data, leadLoops.data],
  );
  const [error, setError] = useState('');
  // Said beside the thing it is about, in the ordinary voice. See onDone below.
  const [notice, setNotice] = useState('');
  /**
   * Which row's Lead Origin cell is open, if any.
   *
   * Held here rather than inside each cell so exactly one is ever open. With
   * per-row state, clicking a second cell leaves the first one sitting as a
   * select nobody closed — 693 rows offering that is a table that looks like
   * it is mid-edit everywhere.
   */
  const [originEditing, setOriginEditing] = useState<string | null>(null);
  const updateLead = useUpdateLead();
  /**
   * Save one lead's origin from the row. '' clears it.
   *
   * Rejects rather than swallowing, so the cell can stop showing itself as
   * saving; the message goes to the same error strip every other write on this
   * page uses, rather than a second notification surface for one field.
   */
  const saveOrigin = useCallback(
    (leadId: string, originId: string) =>
      new Promise<void>((resolve, reject) => {
        setError('');
        updateLead.mutate(
          { leadId, changes: { lead_source_id: originId === '' ? null : originId } },
          {
            onSuccess: () => resolve(),
            onError: (cause) => {
              setError(leadFieldErrorMessage(cause.message));
              reject(cause instanceof Error ? cause : new Error(String(cause)));
            },
          },
        );
      }),
    [updateLead],
  );
  // ?lead=<id> opens that lead's panel on arrival — the address a deal's Lead
  // chip and a Slack paste both use. Read once, the same rule as ?tab and the
  // Deals board's ?deal: closing the panel should not rewrite the URL, and
  // re-reading would reopen it on the next render.
  /**
   * The set somebody drilled into by clicking a number, or null.
   *
   * Page state and NOT the URL. 461 lead ids is sixteen kilobytes of query
   * string, and half-encoding it buys a shareable link that can silently
   * disagree with the number it came from once the data moves underneath it.
   * A refresh drops the set and the tab shows everything again, which is the
   * honest failure; clicking the number again is one click.
   */
  const [drill, setDrill] = useState<Drill | null>(null);

  /**
   * Somebody clicked a number. Land them on the page that lists that kind of
   * record, showing exactly the set behind it.
   *
   * The set REPLACES the filters rather than joining them. The drill was
   * computed from whatever was on screen where it was clicked — the Pipeline's
   * own range and slice — so ANDing it with a stale filter left on the Leads
   * tab would silently subtract from a number the person just read, and the
   * count in the banner would not match the table under it. They can narrow
   * further from there; the first view they get is the number they clicked.
   */
  function openDrill(next: Drill): void {
    setDrill(next);
    setLeadFilter({ ...EMPTY_FILTER, onlyLeadIds: next.ids });
    // Both view modes off, and BOTH matter. groupLeadsByRound keeps only leads
    // currently in a round, so drilling into "Awaiting 1st touch · 26" while
    // Rounds view was on found all 26, said 26 on the tab and in the match
    // badge, and drew an empty table — because none of them is in a round.
    // Kyu hit exactly that on the live page. A view mode that can hide the set
    // is not a preference at this moment, it is a way to make the feature lie.
    //
    // Deliberately not persisted. The drill is page state and a refresh drops
    // it; his Rounds preference should come back with everything else rather
    // than being quietly spent by one click on a tile.
    setGroupByCompany(false);
    setRoundsView(false);
    setTab('leads');
  }

  /**
   * Moving to another tab yourself ends the drill.
   *
   * PipelineView reads `filteredForList`, the SAME narrowed list the Leads tab
   * renders. So a drilled set silently rescoped the whole Pipeline — Kyu drove
   * into a set of 19 and then opened the Pipeline, which read "19 inbound
   * leads · All time" against a database holding 488. Every figure on it was
   * arithmetically correct and the page was a lie, because the one thing that
   * explained the 19 was a banner on a different tab.
   *
   * The set is an answer to "who are these", and that question belongs to the
   * Leads tab. Clicking another tab is moving on from it.
   */
  function goTab(next: TrackerTab): void {
    if (next !== 'leads') {
      clearDrill();
    }
    setTab(next);
  }

  /** Back to an ordinary lead list. The banner goes with the set. */
  function clearDrill(): void {
    setDrill(null);
    setLeadFilter((current) => ({ ...current, onlyLeadIds: null }));
  }

  const [openLeadId, setOpenLeadId] = useState<string | null>(() => searchParams.get('lead'));
  // /tracker?tab=companies&company=<id>, the same shape as ?lead=. The deal
  // drawer's Open link beside its Company field lands here.
  const [requestedCompanyId] = useState<string | null>(() => searchParams.get('company'));
  // The company drawer, openable from the Leads tab since Drop 33 — the lead
  // panel's company name is the door. One profile panel at a time: opening a
  // company closes the lead and vice versa, because two stacked drawers is a
  // navigation with no back button.
  const [openCompanyId, setOpenCompanyId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);

  const closeDetail = useCallback(() => setOpenLeadId(null), []);
  const closeCompany = useCallback(() => setOpenCompanyId(null), []);
  const closeCreate = useCallback(() => setCreating(false), []);
  const closeImport = useCallback(() => setImporting(false), []);

  const stages = useMemo(() => stagesQuery.data ?? [], [stagesQuery.data]);
  const leads = useMemo(() => leadsQuery.data ?? [], [leadsQuery.data]);
  const allMemberships = useMemo(() => allMembershipsQuery.data ?? [], [allMembershipsQuery.data]);
  const openLead = leads.find((l) => l.id === openLeadId) ?? null;
  const openCompany = (companiesQuery.data ?? []).find((c) => c.id === openCompanyId) ?? null;

  if (accessQuery.isPending) {
    return (
      <>
        <PageHeader title="Sales Tracker" topbar />
        <Panel>
          <Skeleton lines={3} label="Checking access" />
        </Panel>
      </>
    );
  }

  if (accessQuery.data !== true) {
    return (
      <>
        <PageHeader title="Sales Tracker" topbar />
        <Panel>
          <EmptyState
            title="Sales team only"
            description="The Sales Tracker is limited to the sales team. Ask an admin if you need access."
          />
        </Panel>
      </>
    );
  }

  function body() {
    if (leadsQuery.isPending || stagesQuery.isPending) {
      return <Skeleton lines={4} label="Loading the tracker" />;
    }
    if (leadsQuery.isError || stagesQuery.isError) {
      return (
        <EmptyState title="The tracker could not be loaded" description="Refresh to try again." />
      );
    }

    if (tab === 'dashboard') {
      return (
        <DashboardView
          leads={leads}
          stages={stages}
          onOpenLead={setOpenLeadId}
          onDrill={openDrill}
        />
      );
    }

    if (tab === 'outreach') return <OutreachView leads={leads} stages={stages} />;
    if (tab === 'companies')
      return (
        <CompaniesView
          leads={leads}
          stages={stages}
          initialCompanyId={requestedCompanyId}
          onOpenLead={(leadId) => {
            // Jump to the lead itself: the company drawer has told you who
            // they are, and the next question is always about the person.
            setOpenLeadId(leadId);
            setTab('leads');
          }}
        />
      );
    if (tab === 'linkedin') return <ProspectingView />;
    if (tab === 'rounds') return <RoundsView />;
    if (tab === 'copy') return <CopyLibraryView leads={leads} />;
    if (tab === 'reports') return <ReportsView leads={leads} stages={stages} />;

    // Needs Review only. Grouping the classified leads would cost a pass over
    // 688 rows to answer a question that is already answered on every one of
    // them.
    const unclassified = leads.filter((lead) => lead.source === null);
    const evidence = evidenceByLead(unclassified, allMemberships, listsQuery.data ?? []);
    const groups = evidenceGroups(
      unclassified,
      allMemberships,
      listsQuery.data ?? [],
      new Map((people.data ?? []).map((person) => [person.id, person.display_name])),
    );

    const filteredForList = filterLeads(
      leads,
      leadFilter,
      stages,
      categoryLinks.data ?? [],
      todayIso(),
      // Already loaded for the Next action column, so the "nothing scheduled"
      // filter costs no extra query — it reads the same answer.
      new Set(nextActions.keys()),
      evidence,
      // Won and Lost are positions read off the deals, so the filter needs the
      // rollup to tell a converted lead from a closed-out one.
      dealOutcomes.data ?? new Map(),
      roundContext,
      // Which origin belongs to which chip. Empty until the vocabulary loads,
      // and the filter treats empty as "do not narrow" rather than "match
      // nothing" — see filterLeads.
      originGrouping,
    );
    // Counted with the tab axis cleared and every other condition kept, so a
    // search narrows the numbers on the tabs too. See leadTabCounts.
    const tabCounts = leadTabCounts(
      leads,
      leadFilter,
      stages,
      categoryLinks.data ?? [],
      todayIso(),
      new Set(nextActions.keys()),
      dealOutcomes.data ?? new Map(),
      roundContext,
      originGrouping,
    );
    // The Pipeline kanban lived here until batch 4. Kyu, in the restructure
    // doc: "Once Deals have their own Pipeline, the current lead Pipeline
    // becomes duplicate and confusing." Deals has its board; what was useful
    // here became Leads filters (the Backlog drawer is the New status, the
    // Stale chip is No touch in 14 days), and dragging a lead between
    // statuses was never how a status should change anyway.

    // Pipeline tab — the SAME filtered list the Leads table renders, and the
    // same filter bar above it. One definition of a slice: a pipeline that
    // disagreed with the list it links to would be worse than no pipeline.
    if (tab === 'pipeline') {
      return (
        // One width for the tab. The filter bar was running the full window
        // while the pipeline below it stopped at 54rem, so the two stacked
        // with different right edges and the Views control ended up marooned
        // a screen away from the chips it sits beside. A bar wider than the
        // thing it filters reads as two unrelated screens.
        <div className="pipeline-tab">
          {/* Literally the Leads tab's bar, not a copy of it. Set a filter
              here and it holds when you switch to Leads, and the other way
              round — because it is one piece of state, so the two screens
              cannot end up describing different people. */}
          <LeadFilterBar
            filter={leadFilter}
            onChange={setLeadFilter}
            stages={stages}
            categories={leadCategories.data ?? []}
            matchCount={filteredForList.length}
            totalCount={leads.length}
            tabCounts={tabCounts}
            origins={leadOrigins.data ?? []}
            closureReasons={closureReasons.data ?? []}
          />
          <PipelineView
            onDrill={openDrill}
            leads={filteredForList}
            stages={stages}
            activities={allActivityQuery.data ?? []}
            prospecting={prospectActivityQuery.data ?? []}
            admin={adminActivityQuery.data ?? []}
            memberships={allMembershipsQuery.data ?? []}
            outcomes={dealOutcomes.data ?? new Map()}
            origins={leadOrigins.data ?? []}
            closureReasons={closureReasons.data ?? []}
            followups={followupsQuery.data ?? []}
            running={runningRounds.data ?? new Map()}
            loops={leadLoops.data ?? new Map()}
            people={people.data ?? []}
            sliceLabel={pipelineSliceLabel(leadFilter, leadOrigins.data ?? [])}
            onOpenLead={setOpenLeadId}
            onPickOrigin={(group, originId) => {
              // Clicking a row in the by-origin table sets the SAME filter the
              // Lead Origin dropdown sets, so the table and the control can
              // never mean different things. Clicking the row that is already
              // chosen clears it — otherwise the only way back is to hunt for
              // the token.
              setLeadFilter((current) =>
                current.originGroup === group && current.originId === originId
                  ? { ...current, originGroup: null, originId: null }
                  : { ...current, originGroup: group, originId },
              );
            }}
          />
        </div>
      );
    }

    // Leads tab
    // Sorted AFTER filtering, so the comparator only ever runs over the rows
    // on screen rather than all 693 on every keystroke in the search box.
    const visible = sortLeads(filteredForList, sort, {
      companyNames: companyNamesById,
      nextActionDue: nextActionDueByLead,
    });
    // Grouped rows, or one group per lead when the toggle is off. Computed
    // here rather than memoised: this body already filters 690 leads on every
    // render, and one pass over the result is not what would need optimising.
    const companyNames = new Map(
      (companiesQuery.data ?? []).map((company) => [company.id, company.name]),
    );
    const leadGroups = roundsView
      ? groupLeadsByRound(visible, roundContext.running, todayIso())
      : groupByCompany
        ? groupLeadsByCompany(visible, companyNames)
        : visible.map((lead) => ({
            key: lead.id,
            kind: 'lone' as const,
            companyId: lead.company_id,
            companyName: '',
            leads: [lead],
            worked: lead.touch_count > 0 ? 1 : 0,
            untouched: lead.touch_count > 0 ? 0 : 1,
          }));
    const duplicates = duplicateLeadGroups(leads);
    const lists = listsQuery.data ?? [];
    const membershipsByLead = new Map<string, Membership[]>();
    for (const membership of allMemberships) {
      membershipsByLead.set(membership.lead_id, [
        ...(membershipsByLead.get(membership.lead_id) ?? []),
        membership,
      ]);
    }
    const listNamesOf = (leadId: string) =>
      (membershipsByLead.get(leadId) ?? [])
        .map((m) => String(lists.find((l) => l.id === m.list_id)?.name ?? ''))
        .filter((name) => name !== '')
        .join(', ');
    // Last touch now comes from the lead's own counters, which the activity
    // timeline maintains. It used to be read off list memberships, so a lead
    // nobody had put on a list read as never touched no matter how many emails
    // had gone out — the same blind spot that made "awaiting first touch"
    // untrustworthy.
    const today = todayIso();
    return (
      <div>
        {drill === null ? null : <DrillBanner drill={drill} onClear={clearDrill} />}
        <LeadFilterBar
          filter={leadFilter}
          onChange={setLeadFilter}
          stages={stages}
          categories={leadCategories.data ?? []}
          matchCount={visible.length}
          totalCount={leads.length}
          tabCounts={tabCounts}
          roundsView={roundsView}
          onToggleRounds={() => {
            const next = !roundsView;
            setRoundsView(next);
            writeRoundsView(next);
            if (next && groupByCompany) {
              setGroupByCompany(false);
              writeGroupByCompany(false);
              setExpandedCompanies(new Set());
            }
          }}
          origins={leadOrigins.data ?? []}
          evidenceGroups={groups}
          closureReasons={closureReasons.data ?? []}
        />
        {/* A VIEW control, not a filter: it changes how the same leads are
            drawn, never which ones match. Off unless somebody turns it on —
            160 of 244 companies have a single lead, so grouping is a job you
            do sometimes, not the shape of the table. */}
        <div className="lead-groupbar">
          <label className="lead-groupbar-toggle">
            <input
              type="checkbox"
              checked={groupByCompany}
              onChange={(event) => {
                setGroupByCompany(event.target.checked);
                writeGroupByCompany(event.target.checked);
                setExpandedCompanies(new Set());
                // One grouping at a time — see the roundsView declaration.
                if (event.target.checked && roundsView) {
                  setRoundsView(false);
                  writeRoundsView(false);
                }
              }}
            />
            Group by company
          </label>
          {!roundsView ? null : (
            <span className="lead-groupbar-note">
              The follow-up queue: only leads currently in a round, worst-off first. The loop chips
              in Filters still cover who needs a next round.
            </span>
          )}
          {!groupByCompany ? null : (
            <span className="lead-groupbar-note">
              {collapsedRowCount(leadGroups)} rows · {visible.length} leads. Nobody is hidden —
              every company row says how many inside it are untouched.
            </span>
          )}
        </div>
        {selectedLeadIds.size === 0 ? null : (
          <div className="lead-bulk-bar">
            <span>{selectedLeadIds.size} selected</span>
            <Button
              size="sm"
              disabled={markContacted.isPending}
              onClick={() => {
                const newStage = newLeadStage(stages);
                const contacted = stageByKey(stages, 'contacted');
                if (newStage === null || contacted === null) {
                  setError('Lead statuses are not loaded.');
                  return;
                }
                markContacted.mutate(
                  {
                    leadIds: [...selectedLeadIds],
                    newStageId: newStage.id,
                    contactedStageId: contacted.id,
                  },
                  {
                    onSuccess: (moved) => {
                      setSelectedLeadIds(new Set());
                      setError(
                        moved === selectedLeadIds.size
                          ? ''
                          : `Marked ${String(moved)} as contacted. The rest were already past New and were left alone.`,
                      );
                    },
                    onError: (cause) => setError(leadFieldErrorMessage(cause.message)),
                  },
                );
              }}
            >
              Mark as contacted
            </Button>
            {/* Filling Lead Source, offered only where it applies: inside
                Needs Review, on leads that have none.

                The strong-evidence group gets ONE button, pre-labelled with
                what its evidence supports — you are confirming a claim you can
                read above it. Every other group gets both buttons and no
                proposal, because being imported together or sitting on a
                nurture list says nothing about who approached whom.

                Kyu: "strong evidence group = confirm system proposal; unclear
                groups = same bulk tool, but manual Inbound/Outbound choice." */}
            {activeLeadTab(leadFilter) !== 'needs-review'
              ? null
              : (() => {
                  const group = groups.find((entry) => entry.key === leadFilter.evidenceGroup);
                  const rule = group?.claim ?? 'Reviewed by hand in Needs Review';
                  const fill = (source: 'inbound' | 'outbound') => {
                    setError('');
                    bulkFill.mutate(
                      { leadIds: [...selectedLeadIds], source, rule },
                      {
                        onSuccess: () => {
                          setSelectedLeadIds(new Set());
                        },
                        onError: (cause) => setError(batchErrorMessage(cause.message)),
                      },
                    );
                  };
                  const sides =
                    group?.proposes === undefined || group.proposes === null
                      ? (['inbound', 'outbound'] as const)
                      : ([group.proposes] as const);
                  return sides.map((side) => (
                    <Button
                      key={side}
                      size="sm"
                      disabled={bulkFill.isPending}
                      onClick={() => fill(side)}
                    >
                      {`Mark all ${side === 'inbound' ? 'Inbound' : 'Outbound'}`}
                    </Button>
                  ));
                })()}
            {/* The other half of the closure filters, and they ship together
                on purpose. A filtered set of leads who said "not right now"
                with no way to act on it is a report; this is what makes it a
                tool. See AddSelectedToList.tsx and leadListSafety.ts — the
                Do-not-contact guard is inside, not optional, and cannot be
                answered until the closing-reason vocabulary has loaded. */}
            <AddSelectedToList
              leads={leads}
              selectedIds={selectedLeadIds}
              lists={listsQuery.data ?? []}
              closureReasons={closureReasons.data ?? []}
              onDone={(message) => {
                // A NOTICE, not an error. This sentence carries the consent
                // count — "1 was left out — Do not contact" — and routing it
                // through setError put it in a red banner at the top of the
                // page while the person was scrolled to the bulk bar. The one
                // line reporting a consent omission was the easiest to miss.
                setNotice(message);
                setError('');
                // The selection is dropped, the same as after a bulk fill.
                // Keeping it would invite the same set going into a second
                // list on the next click, which is how somebody gets two
                // campaigns.
                setSelectedLeadIds(new Set());
              }}
              onError={(message) => {
                setNotice('');
                setError(message);
              }}
            />
            {/* Kyu, 31 Aug: "I just want the team to have that ability to do
                that." The panel says who is being enrolled, who is skipped and
                why, how many emails that is and when the tasks land — all of
                it before anything is written. See StartRoundsForSelected.tsx
                for the night that made it necessary. */}
            <StartRoundsForSelected
              leads={leads}
              selectedIds={selectedLeadIds}
              stages={stages}
              onDone={(message, started) => {
                setNotice(message);
                setError('');
                // Only the leads that actually got a round are dropped. The
                // rest stay ticked, because they are exactly the ones somebody
                // still has to look at, and clearing them would hide the work.
                setSelectedLeadIds((current) => {
                  const next = new Set(current);
                  for (const id of started) {
                    next.delete(id);
                  }
                  return next;
                });
              }}
              onError={(message) => {
                setNotice('');
                setError(message);
              }}
            />
            <Button variant="ghost" size="sm" onClick={() => setSelectedLeadIds(new Set())}>
              Clear selection
            </Button>
            <span className="tracker-field-note">
              {activeLeadTab(leadFilter) === 'needs-review'
                ? 'Only leads with no Lead Source are filled. Anything already answered is left alone.'
                : 'For leads already emailed outside the Hub. Only leads still on New are moved.'}
            </span>
          </div>
        )}
        {notice === '' ? null : <p className="tracker-field-note">{notice}</p>}
        {/* The way back. A bulk fill is cheap to make and would otherwise be
            expensive to unmake — Drop 3c made changing a set Lead Source
            admin-only, one lead at a time, with a typed reason. So a batch is
            recorded as a batch and can be returned to blank in one go.

            8f: the notice goes quiet after thirty seconds, the undo does not.
            See BatchUndoStrip for why those had to be separated. */}
        {!isAdmin ? null : (
          <BatchUndoStrip
            batches={recentBatches.data ?? []}
            pending={revertBatch.isPending}
            onUndo={(batchId, reason, onDone) => {
              setError('');
              revertBatch.mutate(
                { batchId, reason },
                {
                  onSuccess: (count) => {
                    onDone(count);
                    setError(
                      `${String(count)} leads returned to no Lead Source. Anything corrected by hand since was left alone.`,
                    );
                  },
                  onError: (cause) => {
                    setError(batchErrorMessage(cause.message));
                  },
                },
              );
            }}
          />
        )}
        {followUpFor === null ? null : (
          <div className="lead-followup-bar">
            <span className="tracker-field-note">Logged for {followUpFor.name}.</span>
            <FollowUpPrompt
              leadId={followUpFor.id}
              todayIso={today}
              onDone={() => setFollowUpFor(null)}
            />
          </div>
        )}
        {visible.length === 0 ? (
          <EmptyState
            title={leads.length === 0 ? 'No leads yet' : 'No matches'}
            description={
              leads.length === 0
                ? 'Add the first lead.'
                : 'Nothing matches these filters. Clear them, or pick a saved view.'
            }
          />
        ) : (
          <div className="tracker-table-scroll">
            <table className="tracker-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      aria-label="Select every lead shown"
                      checked={visible.length > 0 && selectedLeadIds.size === visible.length}
                      onChange={(event) =>
                        setSelectedLeadIds(
                          event.target.checked
                            ? new Set(visible.map((lead) => lead.id))
                            : new Set(),
                        )
                      }
                    />
                  </th>
                  <th aria-sort={ariaSortFor(sort, 'name')}>
                    <SortHeader column="name" sort={sort} onSort={applySort}>
                      Name
                    </SortHeader>
                  </th>
                  <th aria-sort={ariaSortFor(sort, 'email')}>
                    <SortHeader column="email" sort={sort} onSort={applySort}>
                      Email
                    </SortHeader>
                  </th>
                  <th aria-sort={ariaSortFor(sort, 'company')}>
                    <SortHeader column="company" sort={sort} onSort={applySort}>
                      Company
                    </SortHeader>
                  </th>
                  <th>Status</th>
                  <th>Lead Source</th>
                  <th>Categories</th>
                  <th aria-sort={ariaSortFor(sort, 'lastTouch')}>
                    <SortHeader column="lastTouch" sort={sort} onSort={applySort}>
                      Last Touch
                    </SortHeader>
                  </th>
                  {/* Beside Last Touch on purpose: the cell that reads "never"
                      and the control that fixes it belong in the same glance. */}
                  <th>Log</th>
                  {/* Kyu: "what is the protocol if someone does not answer?
                      when is the next follow-up? who owns it?" — the third
                      question is answered by the assignee on the task, the
                      first two by this column being either filled or empty. */}
                  <th aria-sort={ariaSortFor(sort, 'nextAction')}>
                    <SortHeader column="nextAction" sort={sort} onSort={applySort}>
                      Next action
                    </SortHeader>
                  </th>
                  {/* Beside Next action, because they answer the same question
                      at two scales: one names the single thing somebody
                      committed to, the other says whether this lead is inside
                      the follow-up loop at all. Kyu's rule is that it always
                      should be. */}
                  <th>Rounds</th>
                  <th>Deal outcome</th>
                  <th>Lists</th>
                </tr>
              </thead>
              <tbody>
                {leadGroups.map((group) => {
                  // A lone row is always open — there is nothing to expand.
                  // A round group too: a work queue is worked, not collapsed.
                  const open =
                    group.kind === 'lone' ||
                    group.kind === 'round' ||
                    expandedCompanies.has(group.key);
                  return (
                    <Fragment key={group.key}>
                      {group.kind !== 'round' ? null : (
                        <tr className="lead-group-row lead-group-row--round">
                          <td colSpan={LEAD_TABLE_COLUMNS}>
                            {/* Not a button: there is nothing to expand, and a
                                control that does nothing when pressed teaches
                                people the headers are broken. */}
                            <span className="lead-group-toggle lead-group-static">
                              <span className="lead-group-name">{group.companyName}</span>
                              <span className="lead-group-count">
                                {roundGroupSummary(group, roundContext.running, todayIso())}
                              </span>
                            </span>
                          </td>
                        </tr>
                      )}
                      {group.kind !== 'company' ? null : (
                        <tr className="lead-group-row">
                          <td colSpan={LEAD_TABLE_COLUMNS}>
                            <button
                              type="button"
                              className="lead-group-toggle"
                              aria-expanded={open}
                              onClick={() => {
                                setExpandedCompanies((current) => {
                                  const next = new Set(current);
                                  if (next.has(group.key)) {
                                    next.delete(group.key);
                                  } else {
                                    next.add(group.key);
                                  }
                                  return next;
                                });
                              }}
                            >
                              <span aria-hidden="true" className="section-toggle-caret">
                                {open ? '▾' : '▸'}
                              </span>
                              <span className="lead-group-name">{group.companyName}</span>
                              {/* Never one worked/not-worked state. A company
                                  where one of five has been contacted must not
                                  read as worked, or the other four have been
                                  hidden by the act of tidying up. */}
                              <span className="lead-group-count">{groupSummary(group)}</span>
                              {/* Nearly one company name per lead means the
                                  record is being used as a bucket rather than
                                  holding one company spelled two ways. Named
                                  rather than fixed: which of them really
                                  belongs here is a question for a person. */}
                              {!looksMislinked(group) ? null : (
                                <Badge tone="warning" micro>
                                  Names do not match
                                </Badge>
                              )}
                            </button>
                          </td>
                        </tr>
                      )}
                      {!open
                        ? null
                        : group.leads.map((lead) => (
                            <tr key={lead.id}>
                              <td>
                                <input
                                  type="checkbox"
                                  aria-label={`Select ${leadName(lead)}`}
                                  checked={selectedLeadIds.has(lead.id)}
                                  onChange={(event) => {
                                    const next = new Set(selectedLeadIds);
                                    if (event.target.checked) {
                                      next.add(lead.id);
                                    } else {
                                      next.delete(lead.id);
                                    }
                                    setSelectedLeadIds(next);
                                  }}
                                />
                              </td>
                              {/* Clamped to one line, like the Lists cell above. The Log
                        and Next action columns took width from these two, and
                        a long name or company wrapped to a second line — a
                        55px row beside 40px neighbours, which is the row-height
                        problem Drop 8 fixed. Full text stays on hover. */}
                              <td className="lead-name-cell">
                                <button
                                  type="button"
                                  className="tracker-lead-link cell-clamp cell-clamp--1"
                                  title={leadName(lead)}
                                  onClick={() => setOpenLeadId(lead.id)}
                                >
                                  {leadName(lead)}
                                </button>
                              </td>
                              <td style={{ color: 'var(--text-secondary)' }}>{lead.email}</td>
                              <td style={{ color: 'var(--text-secondary)' }}>
                                {/* Said, not left blank. 228 of 690 leads have neither a
                          linked company nor a name typed in, and a blank cell
                          reads as "not loaded yet" rather than as a fact.
                          Written in the cell rather than into company_name,
                          which is what {{company}} resolves to in email copy. */}
                                {lead.company_id === null && lead.company_name.trim() === '' ? (
                                  <span
                                    style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}
                                  >
                                    No company
                                  </span>
                                ) : (
                                  <span
                                    className="cell-clamp cell-clamp--1"
                                    title={lead.company_name}
                                  >
                                    {lead.company_name}
                                  </span>
                                )}
                              </td>
                              <td>
                                <span className="lead-badges">
                                  {(() => {
                                    // Won and Lost are read off the linked deals, so the
                                    // cell shows where the lead actually is rather than
                                    // where somebody last set it. The title says so,
                                    // because a value that moved on its own owes the
                                    // reader an explanation.
                                    const key = leadStageKey(lead.stage_id, stages);
                                    const position = positionOf(
                                      key,
                                      dealOutcomes.data?.get(lead.id),
                                    );
                                    const derived = positionIsDerived(key, position);
                                    const label = positionLabel(position, stages);
                                    const tone = positionTone(position);
                                    if (tone === null) {
                                      return derived ? (
                                        <span title="Read from the linked deals">{label}</span>
                                      ) : (
                                        label
                                      );
                                    }
                                    return (
                                      <Badge tone={tone}>
                                        <span title="Read from the linked deals">{label}</span>
                                      </Badge>
                                    );
                                  })()}
                                  {/* Nurture is a park, not a problem: a quiet second
                                      line, never a chip. Bad contact IS a problem and
                                      keeps its colour. (docs/design-rule.md) */}
                                  {!lead.nurture_active ? null : (
                                    <span className="lead-sub">Nurture</span>
                                  )}
                                  {!lead.bad_contact ? null : (
                                    <Badge tone="warning" micro>
                                      Bad contact
                                    </Badge>
                                  )}
                                </span>
                              </td>
                              <td className="lead-origin-cell">
                                {/* Lead Source over Lead Origin — the same question
                                    coarse then fine, and the origin editable from the
                                    row. See RowOriginCell for why the source itself is
                                    not, and for the 674-of-693 measurement that made
                                    this worth building. */}
                                <RowOriginCell
                                  lead={lead}
                                  sources={leadOrigins.data ?? []}
                                  editing={originEditing === lead.id}
                                  onOpen={() => setOriginEditing(lead.id)}
                                  onClose={() => setOriginEditing(null)}
                                  onSave={(originId) => saveOrigin(lead.id, originId)}
                                />
                              </td>
                              <td>
                                {/* "CG" for Corporate Gifting — Kyu, 2026-08-29. The
                                    short form is what buys the Name column its width. */}
                                {(() => {
                                  const names = categoriesOf(lead.id, categoryLinks.data ?? []).map(
                                    (id) => nameOf(id, leadCategories.data ?? []),
                                  );
                                  if (names.length === 0) return null;
                                  return (
                                    <span className="lead-plain" title={names.join(', ')}>
                                      {names.map(shortCategory).join(' · ')}
                                    </span>
                                  );
                                })()}
                              </td>
                              <td style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                                {lead.last_touch_at === null ? (
                                  <span className="lead-never">
                                    {lastTouchLabel(lead.last_touch_at, today)}
                                  </span>
                                ) : (
                                  lastTouchLabel(lead.last_touch_at, today)
                                )}
                                {lead.touch_count === 0 ? null : (
                                  <span className="lead-touch-count">
                                    {lead.touch_count} touch{lead.touch_count === 1 ? '' : 'es'}
                                  </span>
                                )}
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {/* Labelled, not hover-revealed. Drop 5 replaced a
                          labelled control with a faint hover arrow and Drop 11
                          had to undo it; a control people cannot see is a
                          control they do not use. */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled={logFromRow.isPending}
                                  onClick={() => {
                                    setError('');
                                    setLoggingLeadId(lead.id);
                                    logFromRow.mutate(
                                      { leadId: lead.id, kind: 'email_sent', body: '' },
                                      {
                                        onSettled: () => setLoggingLeadId(''),
                                        onSuccess: () =>
                                          setFollowUpFor({ id: lead.id, name: leadName(lead) }),
                                        onError: (cause) =>
                                          setError(activityErrorMessage(cause.message)),
                                      },
                                    );
                                  }}
                                >
                                  {loggingLeadId === lead.id ? 'Logging…' : 'Email sent'}
                                </Button>
                              </td>
                              <td style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                                {(() => {
                                  const next = nextActions.get(lead.id);
                                  // Blank, not a dash. Most leads have no committed next
                                  // step and 700 placeholders would bury the ones that
                                  // do — the same call the Deal outcome column makes.
                                  if (next?.due_date == null) return null;
                                  return isOverdue(next.due_date, today) ? (
                                    <Badge tone="warning" micro>
                                      {dueLabel(next.due_date, today)}
                                    </Badge>
                                  ) : (
                                    dueLabel(next.due_date, today)
                                  );
                                })()}
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {(() => {
                                  const running = roundContext.running.get(lead.id);
                                  const loop = roundContext.loops.get(lead.id);
                                  const state = loopStateOf(loop);
                                  // Blank for a lead nobody has started a round on, the
                                  // same call Next action and Deal outcome make: 600
                                  // placeholders would bury the rows that say something.
                                  if (state === 'never-in-a-round') return null;
                                  const progress = progressOf(running, today);
                                  const label = loopCellLabel(state, running, loop);
                                  if (state === 'needs-next-round') {
                                    // The failure state in Kyu's rule, and the only one
                                    // here that is somebody's fault. It carries an
                                    // overdue task, so it gets the same warning tone the
                                    // Next action column uses for one.
                                    return (
                                      <Badge tone="warning" micro>
                                        {label}
                                      </Badge>
                                    );
                                  }
                                  return (
                                    <>
                                      <span className="round-line">
                                        {label}
                                        {isRunningOut(progress) ? (
                                          <Badge tone="warning" micro>
                                            {/* daysToSendIn, not daysLeft: the
                                                latter excludes today, so it
                                                reads "3 days left" on a day you
                                                can still send. */}
                                            {`${String(progress?.daysToSendIn ?? 0)} days left`}
                                          </Badge>
                                        ) : isDueToday(progress) ? (
                                          <Badge tone="neutral" micro>
                                            Due today
                                          </Badge>
                                        ) : null}
                                      </span>
                                      {/* The bar RIPENS rather than judges. Kyu, 29 Aug:
                                          "start from light green to dark green at the very
                                          end. Red can imply lack of action when it's not,
                                          we're just not done yet." So no red anywhere on
                                          it, and the pace mark carries what red used to. */}
                                      <RoundBar progress={progress} />
                                    </>
                                  );
                                })()}
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {(() => {
                                  const outcome = dealOutcomes.data?.get(lead.id);
                                  const tone = dealOutcomeTone(outcome);
                                  // Blank rather than a dash: most leads never had a deal,
                                  // and 700 placeholders would bury the rows that say
                                  // something. Won keeps its colour — it is the change
                                  // worth noticing. Open and Lost are facts, in ink.
                                  if (tone === null) return null;
                                  if (tone === 'success') {
                                    return (
                                      <Badge tone={tone} micro>
                                        {dealOutcomeLabel(outcome)}
                                      </Badge>
                                    );
                                  }
                                  return (
                                    <span
                                      className={`lead-plain${tone === 'neutral' ? ' lead-plain--quiet' : ''}`}
                                    >
                                      {dealOutcomeLabel(outcome)}
                                    </span>
                                  );
                                })()}
                              </td>
                              <td style={{ color: 'var(--text-secondary)' }}>
                                {/* Clamped to one line: a lead in four lists rendered an
                          82px row beside 40px neighbours. The full set is in
                          the lead's own panel. */}
                                <span
                                  className="cell-clamp cell-clamp--1"
                                  title={listNamesOf(lead.id)}
                                >
                                  {listNamesOf(lead.id)}
                                </span>
                              </td>
                            </tr>
                          ))}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Duplicate merge section */}
        {duplicates.length > 0 && (
          <div style={{ marginTop: 'var(--space-8)' }}>
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: 'var(--font-weight-semibold)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'var(--text-muted)',
                margin: '0 0 var(--space-4)',
              }}
            >
              Possible duplicates ({duplicates.length} groups)
            </p>
            {duplicates.map((group) => (
              <div
                key={group[0]?.id}
                style={{
                  background: 'var(--surface-raised)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-4)',
                  marginBottom: 'var(--space-4)',
                }}
              >
                <p
                  style={{
                    margin: '0 0 var(--space-3)',
                    fontWeight: 'var(--font-weight-semibold)',
                    fontSize: 'var(--font-size-sm)',
                  }}
                >
                  {leadName(group[0] as Lead)}
                </p>
                <table className="tracker-table">
                  <tbody>
                    {group.map((lead) => (
                      <tr key={lead.id}>
                        <td>
                          <button
                            type="button"
                            className="tracker-lead-link"
                            onClick={() => setOpenLeadId(lead.id)}
                          >
                            {lead.email === '' ? '(no email)' : lead.email}
                          </button>
                        </td>
                        <td style={{ color: 'var(--text-secondary)' }}>{lead.company_name}</td>
                        <td>
                          {lead.source !== null && (
                            <Badge tone={SOURCE_TONES[lead.source]}>
                              {SOURCE_LABELS[lead.source]}
                            </Badge>
                          )}
                        </td>
                        <td>
                          <Button
                            variant="secondary"
                            size="sm"
                            disabled={
                              operations_enabled
                                ? !journeyAdmin || mergeIntents !== null
                                : merge.isPending || mergeIntents !== null
                            }
                            onClick={() => {
                              const losers = group.filter((c) => c.id !== lead.id);
                              if (!operations_enabled) {
                                setError('');
                                for (const loser of losers)
                                  merge.mutate(
                                    { keeper: lead, loser },
                                    {
                                      onError: (cause) =>
                                        setError(salesErrorMessage(cause.message)),
                                    },
                                  );
                                return;
                              }
                              setMergeIntents(
                                losers.map((loser) => ({
                                  kind: 'merge',
                                  keeperId: lead.id,
                                  loserId: loser.id,
                                  label: `Keep ${leadName(lead)} (${lead.email || 'no email'}); merge ${loser.email || 'no email'} into it`,
                                })),
                              );
                            }}
                          >
                            Keep this one
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <PageHeader title="Sales Tracker" topbar />
      <Panel
        actions={
          <span style={{ display: 'flex', gap: 'var(--space-3)' }}>
            <JourneyPendingActions />
            <Button variant="ghost" onClick={() => setImporting(true)}>
              Import CSV
            </Button>
            <Button size="lg" onClick={() => setCreating(true)}>
              New lead
            </Button>
          </span>
        }
      >
        {/* Underline tab nav */}
        <nav className="tracker-tab-nav">
          {(Object.keys(TAB_LABELS) as TrackerTab[]).map((key) => (
            <button
              key={key}
              type="button"
              className={`tracker-tab-button${tab === key ? ' tracker-tab-button--active' : ''}`}
              onClick={() => goTab(key)}
            >
              {TAB_LABELS[key]}
            </button>
          ))}
        </nav>

        {error !== '' && <InlineError>{error}</InlineError>}
        {body()}
      </Panel>

      {/* Lead slide-in panel */}
      {openLead !== null && (
        <LeadPanel
          // Keyed like the CompanyPanel below: the editable Contact fields
          // hold draft state initialised from the lead they first rendered
          // with, so switching leads while the panel is open (a company
          // drawer chip, Drop 33 cross-navigation) must REMOUNT the panel.
          // Without the key, Michael Jordan's panel showed Andrea Brigman's
          // contact fields — caught live, 28 Aug.
          key={openLead.id}
          lead={openLead}
          stages={stages}
          allMemberships={allMemberships}
          onClose={closeDetail}
          onOpenCompany={(companyId) => {
            // Navigate, don't stack: the lead panel closes as the company
            // opens, and the company's lead chips are the way back.
            setOpenLeadId(null);
            setOpenCompanyId(companyId);
          }}
        />
      )}

      {/* Company profile, reachable from any lead since Drop 33 — not only
          from the Companies tab. Same panel either way. */}
      {openCompany !== null && (
        <CompanyPanel
          key={openCompany.id}
          company={openCompany}
          leads={leads}
          stages={stages}
          onClose={closeCompany}
          onOpenLead={(leadId) => {
            setOpenCompanyId(null);
            setOpenLeadId(leadId);
          }}
        />
      )}

      <NewLeadDrawer open={creating} stages={stages} onClose={closeCreate} />
      {operations_enabled && mergeIntents !== null && (
        <JourneyOperationDialog
          intents={mergeIntents}
          onClose={() => setMergeIntents(null)}
          onSaved={() => setMergeIntents(null)}
        />
      )}
      <ImportDrawer open={importing} leads={leads} stages={stages} onClose={closeImport} />
    </>
  );
}
